import { Transaction } from "./transaction";
import { BankAccountDetails } from "@models/bank_account_details";
import { FiatWallet } from "@models";

export class FiatTransaction extends Transaction {
	private originalAmount: number = null; // Original value of transaction amount when setAmounts method was triggered.
	public requested: boolean = false;
	public payment_reference: string = "";
	private amount_without_fee: string = "";
	private fee: string = "";
	private auth_code: string = "";
	private fiat_wallet_id = "";
	private payment_option_id: string = "";
	private payout_account_id: string = "";
	private payout_provider_id: string = "";
	private triedPin = false;
	private requires_2fa_approval: boolean = false;
	private payment_url: string = "";
	private ccard_digits: string = "";
	private wants_type: string = "";
	private directFiatTransaction: boolean = false;
	private bank_account_details: BankAccountDetails = new BankAccountDetails();

	private _bfc_fee_difference: string = "";

	public get _amount(): number {
		return +this.amount;
	}

	public set _amount(s: number) {
		this.amount = s + "";
	}

	public get _auth_code(): string {
		return this.auth_code;
	}

	public set _auth_code(s: string) {
		this.auth_code = s;
	}

	public get hasBankAccountDetails(): boolean {
		return this.getBankAccountDetails().hasDetails;
	}

	public getDirectFiatTransaction(): boolean {
		return this.directFiatTransaction;
	}

	public setDirectFiatTransaction(b: boolean): void {
		this.directFiatTransaction = b;
	}

	public setWallet(w: FiatWallet): void {
		this.setFiatWalletId(w.getId());
	}

	public switchUserWants(): void {
		this.wants_type = this.wants_type === "gross" ? "net" : "gross";
	}

	public getUserWantsType(): string {
		return this.wants_type;
	}

	public setUserWantsType(s: string): void {
		this.wants_type = s;
	}

	public getBfcDifferenceAmount(): string {
		return this._bfc_fee_difference;
	}

	public setBfcDifferenceAmount(s: string): void {
		this._bfc_fee_difference = s;
	}

	public getAmountWithoutFee(): number {
		if (this.amount_without_fee) {
			return +this.amount_without_fee;
		}
		return +this.amount;
	}

	public setAmountWithoutFee(n: number): void {
		this.amount_without_fee = "" + n;
	}

	public getFee(): number {
		return +this.fee;
	}

	public getCCardDigits(): string {
		return this.ccard_digits;
	}

	public setFee(n: number): void {
		this.fee = n + "";
	}

	public getFiatWalletId(): string {
		return this.fiat_wallet_id;
	}

	public getPaymentOptionId(): string {
		return this.payment_option_id;
	}

	public setPaymentOptionId(s: string): void {
		this.payment_option_id = s;
	}

	public getPayoutAccountId(): string {
		return this.payout_account_id;
	}

	public setPayoutAccountId(s: string): void {
		this.payout_account_id = s;
	}

	public getPayoutProviderId(): string {
		return this.payout_provider_id;
	}

	public setPayoutProviderId(s: string): void {
		this.payout_provider_id = s;
	}

	public stepBack(): void {
		if (
			(this.isType("withdrawal") && this.payout_account_id === "") ||
			(this.isType("deposit") && this.payment_option_id === "")
		) {
			this.fiat_wallet_id = "";
		}

		delete this["amount"];
		this.payout_account_id = "";
		this.payment_option_id = "";
		this.amount = "";
		this.amount_without_fee = "";
		this.fee = "";
	}

	public needsPinConfirmation(): boolean {
		return this.requires_2fa_approval;
	}

	public getPaymentLink(): string {
		return this.payment_url;
	}

	public setPaymentLink(s: string): void {
		this.payment_url = s;
	}

	public setFiatWalletId(s: string): void {
		this.fiat_wallet_id = s;
	}

	public setAmounts(): void {
		const parseNumberToAbsoluteInt = (number: number): number => {
			/** Fix for float number incorrectness for amount of the transaction.
			 * Valid for 2 precision fiat currencies, for example if JPY would be added this func should be refactored. (JPY have 0 precision)
			 * Possible improvements -> interface Money {absoluteAmount: number, precision: number}, then such
			 * interface could be used for calculations.*/
			if (Number.isInteger(number)) {
				return +(number + "00");
			}
			const strNumber: string = "" + number;
			const [int, decimal] = strNumber.split(".");
			const precision = decimal.length;

			const parsedDecimal = precision < 2 ? decimal + "0" : decimal;

			return +(int + parsedDecimal);
		};

		if (this.originalAmount === null) {
			this.originalAmount = this._amount;
		}
		this.setAmountWithoutFee(this.originalAmount);
		const [absoluteAmount, absoluteFee] = [
			parseNumberToAbsoluteInt(this.getAmountWithoutFee()),
			parseNumberToAbsoluteInt(this.getFee())
		];
		this.setAmount((absoluteAmount + absoluteFee) / 100);
	}

	public isCancelable(): boolean {
		return (
			(this.isType("deposit") && this.isStatus("pending")) ||
			(this.isType("withdrawal") && this.isStatus("pending"))
		);
	}

	public isConfirmable(): boolean {
		return this.isType("deposit") && this.isStatus("pending") && this.needsPinConfirmation();
	}

	public isPayable(): boolean {
		return this.isType("deposit") && this.isStatus("waiting_for_payment");
	}

	public hasAction(cancel = false): boolean {
		let action =
			this.isPayable() || this.isConfirmable() || (this.isType("withdrawal") && this.isStatus("pending"));
		if (cancel) {
			action = action || this.isCancelable();
		}
		return action;
	}

	public isUncredited(): boolean {
		const credited = this.isStatus("finished") || (this.isType("withdrawal") && this.isStatus("processing"));
		return !credited;
	}

	public setTriedPin(b: boolean): void {
		this.triedPin = b;
	}

	public getPaymentReference(): string {
		return this.payment_reference;
	}

	public setPaymentReference(s: string): void {
		this.payment_reference = s;
	}

	public getBankAccountDetails(): BankAccountDetails {
		return this.bank_account_details;
	}

	public get areFeesIncluded(): boolean {
		return this.wants_type === "gross";
	}

	public get total(): number {
		if (this.areFeesIncluded) {
			return +this.amount;
		}
		return +this.amount + this.getFee();
	}

	public get userGets(): number {
		if (this.areFeesIncluded) {
			return +this.amount - this.getFee();
		}
		return +this.amount;
	}
}
